import { createHash } from "node:crypto";
import type { Agent } from "node:http";
import type { LoggerImplement, ResponseData } from "@iflyrpa/share";
import axios, {
    type AxiosError,
    type AxiosInstance,
    type AxiosRequestConfig,
    type AxiosResponse,
    type CreateAxiosDefaults,
} from "axios";
import { stringifyError } from "./index";
import { ProxyAgent } from "./proxy";

export class Http {
    private readonly apiClient: AxiosInstance;
    public logger?: LoggerImplement;
    public proxyInfo?: string;
    private readonly platform?: string; // 业务标识（xiaohongshu、douyin、weixin等）
    private readonly agentPromise?: Promise<{
        ip: string;
        port: string;
        agent: Agent | null;
    } | null> = undefined;

    static handleApiError(error: unknown): ResponseData {
        // 如果错误已经是 ResponseData 格式，直接抛出
        if (error && typeof error === "object" && "code" in error && "message" in error) {
            return error as ResponseData;
        }

        // 处理其他类型的错误
        return {
            code: 500,
            message: error instanceof Error ? error.message : "未知错误",
            data: error,
        };
    }

    constructor(
        config?: CreateAxiosDefaults,
        logger?: LoggerImplement,
        addr?: string | null,
        accountId?: number,
        platform?: string, // 业务标识，如 "xiaohongshu"、"douyin"、"weixin" 等
    ) {
        this.apiClient = axios.create({
            ...config,
            // proxy: {
            //     host: "127.0.0.1", // 根据你的代理服务设置
            //     port: 8888,
            //     protocol: "http", // 可选
            // },
        });
        this.logger = logger;
        this.platform = platform;

        if (addr) this.agentPromise = ProxyAgent({ logger }, addr, accountId);

        this.addResponseInterceptor(() => undefined);
    }

    public clearResponseInterceptors(): void {
        const interceptors = axios.interceptors.response as unknown as {
            handlers: unknown[];
            clear: () => void;
        };

        // 清除之前的拦截器
        if (interceptors.handlers.length > 0) {
            axios.interceptors.response.clear();
        }
    }

    // 添加响应拦截器
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    public addResponseInterceptor<T = any>(findError: (response: AxiosResponse<T>) => ResponseData | undefined): void {
        const interceptors = this.apiClient.interceptors.response as unknown as {
            handlers: unknown[];
            clear: () => void;
        };

        // 清除之前的拦截器，避免重复添加
        if (interceptors.handlers.length > 0) {
            this.apiClient.interceptors.response.clear();
        }

        this.apiClient.interceptors.response.use(
            (response) => {
                // 检查响应中的 errno 字段
                const error = findError(response);

                if (error) {
                    return Promise.reject(
                        Object.assign(error, {
                            extra: {
                                method: response.config.method,
                                url: response.config.url,
                            },
                        }),
                    );
                }

                return response;
            },
            (error: AxiosError) => {
                const foundError = findError(error.response as AxiosResponse<T>);

                // 创建标准错误响应格式
                const errorResponse: ResponseData = {
                    // 单独拦截401的状态码，映射为400，避免触发绘文平台的鉴权错误处理逻辑
                    code: error.response?.status || 500,
                    message: error.message || "",
                    data: error.response?.data,
                    extra: {
                        method: error.config?.method,
                        url: error.config?.url,
                        isProxyRequest: !!(error.config && (error.config.httpAgent || error.config.httpsAgent)),
                        params: error.config?.params,
                        data: error.config?.data,
                        serverDate: error.response?.headers?.date, // 服务端时间（用于时钟漂移检测）
                    },
                };

                if (foundError) Object.assign(errorResponse, foundError);

                if (error.response?.status === 403) {
                    this.logger?.warn(`[403 详细信息] URL: ${error.config?.url}`);
                    this.logger?.warn(`[403 响应头] ${JSON.stringify(error.response?.headers || {})}`);
                    this.logger?.warn(`[403 响应体] ${JSON.stringify(error.response?.data || null)}`);
                    const verifyDecision = error.response?.headers?.["x-tt-verify-passport-decision"];
                    if (verifyDecision) {
                        this.logger?.warn(`[403 验证决策] x-tt-verify-passport-decision: ${verifyDecision}`);
                    }
                }

                // 如果服务端返回了详细错误信息，可以使用服务端的错误信息
                if (error.response?.data) {
                    if (typeof error.response.data === "object") {
                        // 如果服务端返回的是对象，可以尝试提取错误信息
                        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
                        const serverError = error.response.data as any;

                        errorResponse.message = serverError.msg || serverError.message; // 匹配小红书与普通错误message

                        // 可以保留原始错误数据
                        errorResponse.data = serverError;
                    }
                }
                let _message = error.message || "";
                // ✅ 情况二：网络错误（没有 response）
                if (error.code) {
                    errorResponse.code = errorResponse.code === 401 ? 414 : errorResponse.code; // 屏蔽401错误，避免触发绘文平台鉴权逻辑

                    switch (error.code) {
                        case "ECONNABORTED":
                            _message = "请求超时，请检查网络连接！";
                            break;
                        case "ECONNRESET":
                            _message = "连接被重置，请检查网络或稍后重试！";
                            break;
                        case "ENOTFOUND":
                            _message = "域名解析失败，请检查 URL 是否正确！";
                            break;
                        case "ETIMEDOUT":
                            _message = "网络请求超时，请检查网络连接！";
                            break;
                        case "ECONNREFUSED":
                            _message = "服务器拒绝连接，请稍后重试！";
                            break;
                        case "EAI_AGAIN":
                            _message = "DNS 查询超时，请稍后重试！";
                            break;
                        case "ERR_BAD_REQUEST":
                            _message = "请求出现错误，请检查请求参数！";
                            break;
                        case "ERR_BAD_RESPONSE":
                            _message = `服务器响应异常 (${error.response?.status ?? "unknown"})，请稍后重试！`;
                            break;
                        case "ERR_CANCELED":
                            errorResponse.code = 414; // 阻止前端报错
                            _message = "请求连接超时，请稍候重试！";
                            if (errorResponse.extra?.isProxyRequest) {
                                _message = "代理连接建立超时，请更换区域或稍后重试！";
                                errorResponse.code = 599;
                                errorResponse.data = this.proxyInfo
                                    ? createHash("md5").update(this.proxyInfo.split(":")[0]).digest("hex") +
                                      this.proxyInfo.split(":")[1]
                                    : {};
                            }
                            break;
                        default:
                            this.logger?.debug(`未处理的网络错误代码: ${error.code} ${error.message}`, {
                                errorString: stringifyError(error),
                            });
                            _message = `网络错误: ${error.message}`;
                            break;
                    }
                }
                // 网络层错误（无服务端响应体）：errorResponse.message 此时仍是 axios 原始文案
                // （如 "timeout of 3000ms exceeded"），应优先使用上面归一化后的友好文案 _message。
                // 有服务端响应体时才保留服务端返回的 message。
                if (error.code && !error.response?.data) {
                    errorResponse.message = _message || errorResponse.message;
                } else {
                    errorResponse.message = errorResponse.message ? errorResponse.message : _message;
                }

                if (errorResponse.message === "canceled") {
                    const message = errorResponse.extra?.isProxyRequest
                        ? "代理连接建立超时，请更换区域或稍后重试！"
                        : "网络请求超时，请稍后重试！";
                    errorResponse.message = message;
                }

                if (error.message.includes("Proxy connection ended")) {
                    // 代理Connect阶段错误重写！
                    errorResponse.message = "所在区域代理连接超时，请更换区域或稍后重试！";
                }

                // 返回标准化的错误响应
                throw errorResponse;
            },
        );
    }

    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    public async api<T = any>(
        config: AxiosRequestConfig,
        options?: {
            retries?: number;
            retryDelay?: number;
            timeout?: number;
        },
    ): Promise<T> {
        const retries = options?.retries ?? 0; // 默认不重试
        const retryDelay = options?.retryDelay ?? 500;
        const reqTimeout = options?.timeout ?? 30 * 1000; // 10s 默认超时
        let agent: { ip: string; port: string; agent: Agent | null } | null | undefined;
        // let isForceRefreshProxy = false;
        const sessionRt = async (Rtimes: number): Promise<T> => {
            try {
                // if (this.ip) {
                // 	const cacheKey = `${this.ip}|${this.adr ?? ""}|${this.accountId ?? ""}`;
                //
                //     if( this.adr && Rtimes === 1 ) {
                //         this.adr = "";
                //         Http.agentCache.delete(cacheKey);
                //     }
                //     if( Rtimes%2 === 0 && Rtimes > 0 ) {
                //         // 重试时清除缓存，并强制刷新代理 refresh = true
                //         Http.agentCache.delete(cacheKey);
                //         isForceRefreshProxy = true;
                //         this.task.debug && this.task.logger.info(`进入第${Rtimes+1}次重试，强制刷新代理！`);
                //     }
                //
                //     if (Http.agentCache.has(cacheKey)) {
                //         this.agent = Http.agentCache.get(cacheKey) ?? null;
                //
                //     } else if (Http.agentPromiseCache.has(cacheKey)) {
                //         this.task.debug && this.task.logger.info("代理正在创建，等待中...");
                //         const agentResult = await Http.agentPromiseCache.get(cacheKey);
                //         this.agent = agentResult?.agent ?? null;
                //     } else {
                //         const promise = ProxyAgent(this.ip, this.adr, this.accountId, isForceRefreshProxy);
                //
                //         Http.agentPromiseCache.set(cacheKey, promise);
                //
                //         this.agent = (await promise)?.agent;
                //
                //         if (this.agent) {
                //             this.agent.options.keepAlive = true;
                //             Http.agentCache.set(cacheKey, this.agent);
                //         } else {
                //             Http.agentCache.set(cacheKey, null);
                //         }
                //         Http.agentPromiseCache.delete(cacheKey);
                //     }
                // }
                //
                // if (this.agent === null) {
                //     if (Rtimes === retries && this.ip) {
                //         return Promise.reject({
                //             code: 400,
                //             message: "",
                //             data: {
                //                 code: 400, // 仅配合sessioncheck使用
                //                 msg: "当前所选区域暂不支持!"
                //             }
                //         });
                //     }
                //     if (this.adr) {
                //        throw {code: 500, message: "", data: {}}; // 触发重试
                //     }
                // }

                this.proxyInfo = agent ? `${agent.ip}:${agent.port}` : undefined;

                // 请求级别超时控制（即使代理层没超时，也能强制中断）
                // 注意：AbortController 的兜底超时必须比 axios 自身 timeout 略长，
                // 否则二者同时到点时 AbortController 会先触发 ERR_CANCELED(code 414)，
                // 导致超时错误落不进重试白名单 [0,500,502,503,504,599]，使 retries 形同虚设。
                // 让 axios 先以 ECONNABORTED(code 500) 触发常规 socket 超时即可正常重试，
                // AbortController 仅作为 DNS/连接阶段卡死的最终兜底。
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), reqTimeout + 500); // 全阶段超时兜底

                const response = await this.apiClient<T>({
                    ...config,
                    timeout: reqTimeout, // Axios 自身的超时 http阶段，tcp socket已建立情况下的超时
                    signal: controller.signal, // 自定义超时保护信号，用户axios监听
                    ...(agent ? { httpAgent: agent.agent, httpsAgent: agent.agent } : {}),
                }).finally(() => {
                    clearTimeout(timeoutId);
                });

                return response.data;
            } catch (error) {
                const handledError: ResponseData = Http.handleApiError(error);
                const isRetry = [0, 500, 502, 503, 504, 599].includes(handledError.code);

                if (Rtimes < retries && isRetry) {
                    const url = config.url || "";
                    this.logger?.warn(`进入第${Rtimes + 1}次重试！错误码: ${handledError.code}, 请求地址: ${url}`);
                    await new Promise((resolve) => setTimeout(resolve, retryDelay));
                    return sessionRt(Rtimes + 1);
                }
                return Promise.reject(handledError);
            }
        };
        if (this.agentPromise) {
            const requestUrl = config.url || "";
            const platformTag = this.platform ? `[${this.platform}]` : "";
            this.logger?.info(`${platformTag}[代理请求] 接口: ${requestUrl}`);
            agent = await this.agentPromise;
            if (agent) {
                this.logger?.info(`${platformTag}[代理请求] 代理地址: ${agent.ip}:${agent.port}`);
            }
        }

        return sessionRt(0);
    }
}
